import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { ShortEnquiryForm } from "@/components/forms/ShortEnquiryForm";
import { SITE, emailOf } from "@/content/site";

const Block = ({ icon: Icon, title, children, testId }) => (
  <Reveal className="border-b border-[#8EA3BC]/40 py-7" data-testid={testId}>
    <h2 className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#A27D17]"><Icon className="h-4 w-4" aria-hidden="true" />{title}</h2>
    <div className="mt-3 text-[#1E293B]">{children}</div>
  </Reveal>
);

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  useSeo("contact");
  return (
    <>
      <PageHero eyebrow={c.eyebrow} title={c.title} img="/img/dusk.jpg" testId="contact-hero" />
      <section className="bg-white py-20 lg:py-28" data-testid="contact-page">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="border-t border-[#8EA3BC]/40 lg:col-span-5">
            <Block icon={MapPin} title={c.head} testId="contact-head-office"><p className="font-serif text-xl text-[#1E3A5F]">{c.headAddr}</p></Block>
            <Block icon={MapPin} title={c.ops} testId="contact-operations">
              <ul className="space-y-1">{c.opsList.map((o) => <li key={o}>{o}</li>)}</ul>
            </Block>
            {SITE.phone && (
            <Block icon={Phone} title={`${c.phone} · ${c.whatsapp}`} testId="contact-phone">
              <a href={SITE.phoneHref} dir="ltr" className="block link-underline" data-testid="contact-phone-link">{SITE.phone}</a>
              {SITE.whatsapp && <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" dir="ltr" className="mt-1 block link-underline" data-testid="contact-whatsapp-link">WhatsApp {SITE.whatsapp}</a>}
            </Block>
            )}
            {SITE.emailsLive && (
            <Block icon={Mail} title={c.emails} testId="contact-emails">
              <dl className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-2">
                {SITE.emails.map((b) => (
                  <div key={b} className="contents">
                    <dt className="whitespace-nowrap text-[#4A5D75]">{c.emailLabels[b]}</dt>
                    <dd><a href={`mailto:${emailOf(b)}`} dir="ltr" className="link-underline" data-testid={`contact-email-${b}`}>{emailOf(b)}</a></dd>
                  </div>
                ))}
              </dl>
            </Block>
            )}
            <Block icon={Clock} title={c.hours} testId="contact-hours">
              <p>{c.hoursVal}</p>
              <p className="mt-1 text-[#4A5D75]">{c.hoursNote}</p>
            </Block>
          </div>
          <div className="lg:col-span-7">
            <Eyebrow>{c.formTitle}</Eyebrow>
            <div className="mt-8"><ShortEnquiryForm kind="contact" testId="contact-form" /></div>
          </div>
        </div>
      </section>
      <section className="border-t border-[#8EA3BC]/40" data-testid="contact-map">
        <iframe title={c.mapTitle} loading="lazy" className="block h-[420px] w-full grayscale-[40%]"
          src="https://www.openstreetmap.org/export/embed.html?bbox=19.95%2C32.04%2C20.20%2C32.18&layer=mapnik&marker=32.115%2C20.07" />
      </section>
    </>
  );
}
